// src/pages/MyJobs.js — modern 2025 polish ✨
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import socket from "../socket";
import {
  MapPin,
  Clock,
  User,
  DollarSign,
  MessageSquare,
  CheckCircle,
  XCircle,
  Edit3,
  AlertTriangle,
  RefreshCw,
  X,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const API_WRK = process.env.REACT_APP_API_WRK;
const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:5000";
const API_JOB = `${API_WRK}/job`;
const API_BID = `${API_WRK}/job/bid`;

const MyJobs = () => {
  const workerId = localStorage.getItem("workerId");
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [jobDetails, setJobDetails] = useState({});
  const [bidDetails, setBidDetails] = useState({});
  const [showChangeModal, setShowChangeModal] = useState(null);
  const [newBidAmount, setNewBidAmount] = useState("");
  const [newBidMessage, setNewBidMessage] = useState("");
  const [selectedTab, setSelectedTab] = useState("active");
  const [showConfirm, setShowConfirm] = useState(null);

  // ==================== FETCH JOBS ====================
  useEffect(() => {
    if (workerId) fetchJobs();
  }, [workerId]);

  const fetchJobs = async () => {
    try {
      const res = await axios.get(`${API_WRK}/job/assigned/${workerId}`);
      const allJobs = res.data || [];
      setJobs(allJobs);

      for (let job of allJobs) {
        fetchBidDetails(job._id);
        fetchCustomerDetails(job.customerId, job._id);
      }
    } catch (err) {
      console.error("Error fetching jobs:", err);
    } finally {
      setLoading(false);
    }
  };

  // Extract city + province from full address
  const extractCityProvince = (fullAddress) => {
    if (!fullAddress) return "";

    const parts = fullAddress.split(",");
    if (parts.length < 3) return fullAddress;

    // Example: "13615 Howey Road, Surrey, British Columbia, V3R 5K7"
    const city = parts[1]?.trim();
    const province = parts[2]?.trim();

    return `${city}, ${province}`;
  };

  const fetchBidDetails = async (jobId) => {
    try {
      const res = await axios.get(`${API_BID}/${jobId}/${workerId}`);
      setBidDetails((prev) => ({ ...prev, [jobId]: res.data }));
    } catch {}
  };

  const fetchCustomerDetails = async (customerId, jobId) => {
    try {
      // 🧠 Some jobs store full object, others just store ID
      const id = customerId?._id || customerId;

      // 🛑 Skip invalid or null IDs
      if (!id || id === "null" || id === "undefined") {
        console.warn(
          "⚠️ Skipping invalid customer ID for job:",
          jobId,
          customerId
        );
        return;
      }

      const res = await axios.get(`${API_BASE}/api/customers/profile/${id}`);
      setJobDetails((prev) => ({
        ...prev,
        [jobId]: { ...(prev[jobId] || {}), customer: res.data },
      }));
    } catch (err) {
      console.error("❌ Error fetching customer details:", err.message);
    }
  };

  // ==================== SOCKETS ====================
  useEffect(() => {
    if (!workerId) return;

    socket.emit("registerWorker", { workerId });
    const safeFetch = () => {
      clearTimeout(window._fetchTimer);
      window._fetchTimer = setTimeout(fetchJobs, 400);
    };

    const handleJobUpdate = (update) => {
      if (!update) return;

      switch (update.status) {
        case "worker_completed":
          toast("🛠 You marked the job complete — waiting for customer.");
          break;

        case "completed":
        case "customer_confirmed":
        case "auto_confirmed":
        case "released": // admin / system released payout
          toast.success("✅ Job completed & payment processed!");
          break;

        case "dispute":
          toast.error("⚠️ Job under dispute. Admin reviewing.");
          break;

        case "refunded":
          toast.error("💸 Dispute resolved: customer was refunded.");
          break;

        case "partial_refund":
          toast("⚖️ Dispute resolved with partial refund. Check details.");
          break;

        case "cancelled":
        case "cancelled_by_customer":
          toast.error("❌ Job was cancelled.");
          break;

        default:
          toast(`⚡ Job updated: ${update.status}`);
      }

      safeFetch();
    };

    const handleBidChangeUpdate = (data) => {
      if (String(data.workerId) === String(workerId)) {
        if (data.status === "approved")
          toast.success(
            `💰 Your bid change for "${data.jobTitle}" was accepted!`
          );
        else if (data.status === "rejected")
          toast.error(
            `❌ Your bid change for "${data.jobTitle}" was rejected.`
          );
        safeFetch();
      }
    };

    socket.on("job:update", handleJobUpdate);
    socket.on("bid:changeUpdate", handleBidChangeUpdate);
    socket.on("worker:refresh", safeFetch);
    socket.on("connect", () => socket.emit("registerWorker", { workerId }));

    return () => {
      socket.off("job:update");
      socket.off("bid:changeUpdate");
      socket.off("worker:refresh");
      socket.off("connect");
      clearTimeout(window._fetchTimer);
    };
  }, [workerId]);

  // ==================== ACTIONS ====================
  const handleComplete = async (job) => {
    try {
      await axios.post(`${API_WRK}/job/worker-complete/${job._id}`, {
        workerId,
        customerId: job.customerId._id || job.customerId, // ✅ add this
      });
      toast.success("✅ Job marked complete! Awaiting customer confirmation.");
      fetchJobs();
    } catch (err) {
      console.error("❌ Worker complete error:", err);
      toast.error(
        err?.response?.data?.message ||
          "Error completing job. Try again after scheduled time."
      );
    }
  };

  const handleCancel = async (jobId) => {
    try {
      await axios.post(`${API_JOB}/cancel-by-worker`, { jobId, workerId });
      toast("⚠️ Job cancelled.", { icon: "⚠️" });
      fetchJobs();
    } catch {
      toast.error("Error cancelling job");
    }
  };

  const handleSubmitBidChange = async (jobId, bidId) => {
    if (!newBidAmount) return toast.error("Enter a new bid amount");
    try {
      await axios.post(`${API_JOB}/bids/change`, {
        bidId,
        newAmount: newBidAmount,
        message: newBidMessage,
      });
      toast.success("Bid change request sent ✅");
      setShowChangeModal(null);
      setNewBidAmount("");
      setNewBidMessage("");
      fetchJobs();
    } catch {
      toast.error("Failed to change bid");
    }
  };

  // ⭐ Allow completion ONLY when job date/time has arrived
  const canMarkComplete = (scheduledAt) => {
    if (!scheduledAt) return false;
    const now = new Date();
    const jobDate = new Date(scheduledAt);
    return now >= jobDate;
  };

  // ==================== UI ====================
  if (loading)
    return (
      <div className="flex items-center justify-center h-screen text-gray-500">
        Loading jobs...
      </div>
    );

  const activeJobs = jobs.filter((j) =>
    ["assigned", "worker_completed"].includes(j.status)
  );
  const completedJobs = jobs.filter((j) =>
    ["completed", "customer_confirmed", "auto_confirmed"].includes(j.status)
  );
  const disputedJobs = jobs.filter((j) =>
    ["dispute", "disputed", "cancelled"].includes(j.status)
  );

  const tabButton = (id, label, color) => (
    <button
      onClick={() => setSelectedTab(id)}
      className={`px-6 py-2 rounded-full font-medium transition-all ${
        selectedTab === id
          ? `${color} text-white shadow-lg`
          : "text-gray-700 hover:bg-gray-200"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-50 p-4 md:p-6">
      {/* Header Tabs */}
      <div className="flex justify-center mb-6">
        <div className="bg-white/80 backdrop-blur-lg border border-gray-200 rounded-full p-1 shadow-md flex">
          {tabButton("active", "Active Jobs", "bg-blue-600")}
          {tabButton("completed", "Completed", "bg-green-600")}
          {tabButton("disputed", "Disputed", "bg-red-600")}
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={selectedTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.25 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
        >
          {selectedTab === "active" &&
            activeJobs.map((job) => renderJob(job, "active"))}
          {selectedTab === "completed" &&
            completedJobs.map((job) => renderJob(job, "completed"))}
          {selectedTab === "disputed" &&
            disputedJobs.map((job) => renderJob(job, "disputed"))}
          {selectedTab === "active" &&
            !activeJobs.length &&
            emptyState("No active jobs")}
          {selectedTab === "completed" &&
            !completedJobs.length &&
            emptyState("No completed jobs")}
          {selectedTab === "disputed" &&
            !disputedJobs.length &&
            emptyState("No disputed jobs")}
        </motion.div>
      </AnimatePresence>

      {/* Floating Modal for Change Bid */}
      <AnimatePresence>
        {showChangeModal && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowChangeModal(null)}
            />
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="fixed inset-x-4 top-[50%] z-50 -translate-y-1/2 bg-white rounded-2xl p-6 w-auto max-w-md shadow-2xl border border-gray-200 safe-area-adjust"
            >
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <RefreshCw className="text-blue-600" size={18} /> Change Your Bid
                </h3>
                <button
                  onClick={() => setShowChangeModal(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={20} />
                </button>
              </div>

              <input
                type="number"
                placeholder="New amount"
                value={newBidAmount}
                onChange={(e) => setNewBidAmount(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 mb-2 focus:ring-2 focus:ring-blue-500 outline-none"
              />
              <textarea
                placeholder="Optional message"
                value={newBidMessage}
                onChange={(e) => setNewBidMessage(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 mb-4 focus:ring-2 focus:ring-blue-500 outline-none"
                rows={3}
              />
              <div className="flex gap-3">
                <button
                  onClick={() =>
                    handleSubmitBidChange(
                      showChangeModal.jobId,
                      showChangeModal.bidId
                    )
                  }
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-md font-semibold"
                >
                  Submit
                </button>
                <button
                  onClick={() => setShowChangeModal(null)}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 rounded-md font-semibold"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );

  // ==================== COMPONENTS ====================
  function emptyState(text) {
    return (
      <div className="text-center text-gray-500 py-16 col-span-full">
        <AlertTriangle className="mx-auto mb-2 w-6 h-6 opacity-60" />
        {text}
      </div>
    );
  }

  function renderJob(job, mode) {
    const bid = bidDetails[job._id];
    const customer = jobDetails[job._id]?.customer;

    const isWorkerCompleted =
      job.status === "worker_completed" ||
      job.status === "completed" ||
      job.status === "customer_confirmed" ||
      job.status === "auto_confirmed";

    return (
      <motion.div
        key={job._id}
        layout
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={`rounded-2xl p-5 border shadow-sm hover:shadow-md ${
          mode === "completed"
            ? "bg-green-50 border-green-200"
            : mode === "disputed"
            ? "bg-red-50 border-red-200"
            : "bg-white border-gray-200"
        }`}
      >
        <h2 className="text-lg font-semibold text-gray-900 mb-2">
          {job.jobTitle}
        </h2>
        <p className="text-sm text-gray-700 mb-3 line-clamp-2">
          {job.description}
        </p>
        <div className="text-sm text-gray-600 space-y-1">
          <p className="flex items-center gap-2">
            <MapPin size={16} /> {extractCityProvince(job.location)}
          </p>

          <p className="flex items-center gap-2">
            <Clock size={16} />{" "}
            {job.scheduledAt
              ? new Date(job.scheduledAt).toLocaleString()
              : "N/A"}
          </p>
          {customer && (
            <>
              <p className="flex items-center gap-2">
                <User size={16} /> {customer.name}
              </p>

              {/* 🔥 Show phone ONLY for active jobs */}
              {mode === "active" && customer.phone && (
                <a
                  href={`tel:${customer.phone}`}
                  className="flex items-center gap-2 text-blue-600 hover:text-blue-800 underline"
                >
                  📞 {customer.phone}
                </a>
              )}
            </>
          )}

          {bid && (
            <p className="flex items-center gap-2">
              <DollarSign size={16} /> ${bid.price}
            </p>
          )}
        </div>

        {/* 🔹 Bid change status */}
        {bid?.changeRequest && (
          <div
            className={`mt-3 rounded-lg p-3 border ${
              bid.changeRequest.status === "approved"
                ? "bg-green-50 border-green-300"
                : bid.changeRequest.status === "pending"
                ? "bg-yellow-50 border-yellow-300"
                : "bg-red-50 border-red-300"
            }`}
          >
            <p className="text-sm font-medium">
              💬 Change Request: ${bid.changeRequest.newPrice}
            </p>
            {bid.changeRequest.message && (
              <p className="text-xs italic text-gray-700 mt-1">
                “{bid.changeRequest.message}”
              </p>
            )}
            <p className="text-xs text-gray-600 mt-1 capitalize">
              Status: {bid.changeRequest.status}
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="mt-4 flex flex-wrap gap-2">
          {/* Chat only for ACTIVE jobs */}
          {mode === "active" && (
            <Link
              to={`/chat/${workerId}/${
                job.customerId._id || job.customerId
              }/${job._id}`}
              className="flex-1 flex items-center justify-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-semibold"
            >
              <MessageSquare size={16} /> Chat
            </Link>
          )}

          {/* Hide other buttons if already completed */}
          {!isWorkerCompleted && mode === "active" && (
            <>
              {/* ⭐ ONLY show complete when job time arrives */}
              {canMarkComplete(job.scheduledAt) ? (
                <button
                  onClick={() => setShowConfirm({ type: "complete", job })}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-semibold"
                >
                  <CheckCircle size={16} /> Complete
                </button>
              ) : (
                <button
                  onClick={() => setShowConfirm({ type: "not_allowed", job })}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md text-sm font-semibold hover:bg-gray-400 transition"
                >
                  <Clock size={16} /> Not Allowed Yet
                </button>
              )}

              <button
                onClick={() => setShowConfirm({ type: "cancel", job })}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-semibold"
              >
                <XCircle size={16} /> Cancel
              </button>

              {bid && (
                <button
                  onClick={() =>
                    setShowChangeModal({ jobId: job._id, bidId: bid._id })
                  }
                  className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-md text-sm font-semibold"
                >
                  <Edit3 size={16} /> Change Bid
                </button>
              )}
            </>
          )}

          {isWorkerCompleted && (
            <div className="absolute top-3 right-4 flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-semibold text-green-800 bg-gradient-to-r from-green-100 to-green-200 shadow-md border border-green-300 animate-pulse-slow transition-opacity duration-500 opacity-100">
              <CheckCircle size={16} className="text-green-600" />
              Completed
            </div>
          )}

          {mode === "disputed" && (
            <p className="text-red-600 font-semibold flex items-center gap-2 text-sm mt-2">
              <AlertTriangle size={16} /> Under Dispute
            </p>
          )}
        </div>

        {/* Floating Confirm Modal */}
        <AnimatePresence>
          {showConfirm && (
            <>
              <motion.div
                className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setShowConfirm(null)}
              />
              <motion.div
                initial={{ scale: 0.9, opacity: 0, y: 24 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 24 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-x-4 top-[50%] z-50 -translate-y-1/2 bg-white rounded-2xl p-6 w-auto max-w-md shadow-2xl border border-gray-200 safe-area-adjust"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {showConfirm.type === "complete"
                    ? "Mark this job as complete?"
                    : showConfirm.type === "not_allowed"
                    ? "Not Allowed Yet"
                    : "Cancel this job?"}
                </h3>

                <p className="text-sm text-gray-600 mb-5">
                  {showConfirm.type === "complete"
                    ? "Confirm you’ve finished this task so the customer can review and release payment."
                    : showConfirm.type === "not_allowed"
                    ? "You can only mark this job complete on the scheduled day."
                    : "This will notify the customer and remove the job from your active list."}
                </p>

                <div className="flex gap-3">
                  {showConfirm.type !== "not_allowed" && (
                    <button
                      onClick={() => {
                        if (showConfirm.type === "complete")
                          handleComplete(showConfirm.job);
                        else handleCancel(showConfirm.job._id);
                        setShowConfirm(null);
                      }}
                      className={`flex-1 py-2 rounded-md font-semibold text-white ${
                        showConfirm.type === "complete"
                          ? "bg-green-600 hover:bg-green-700"
                          : "bg-red-600 hover:bg-red-700"
                      }`}
                    >
                      Yes,{" "}
                      {showConfirm.type === "complete" ? "Complete" : "Cancel"}
                    </button>
                  )}

                  <button
                    onClick={() => setShowConfirm(null)}
                    className="flex-1 py-2 rounded-md font-semibold bg-gray-200 hover:bg-gray-300 text-gray-800"
                  >
                    No, Go Back
                  </button>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </motion.div>
    );
  }
};

export default MyJobs;
